import { Session } from 'next-auth';
import { permanentRedirect, redirect } from 'next/navigation';

import { getProductAnimco } from '@services/animco';
import { cmsService } from '@services/cms';
import { getAppConfig } from '@services/config';
import { logger } from '@services/loggers';
import { personServices } from '@services/person';
import { getPlanTypeFromUrlKey } from '@services/products';
import { productDetailsService } from '@services/products/product-details';

import { PalpatineProduct } from '@app-types/api/palpatine';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { ProductNotFoundError } from '@app-types/error';
import { SearchParamsType } from '@app-types/page';
import { ProductParentFull } from '@app-types/product';
import { UserType } from '@app-types/user';

import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';

import { getCurrentProduct, handleProductNotFound } from '@product-detail/helpers';

import { ProductDetail } from './product-detail';

interface PhoneDetailsProps {
    isRenewal?: boolean;
    params: { slug: string };
    searchParams?: SearchParamsType;
    contract?: ContractType;
    session?: Session;
}

const appConfig = getAppConfig();

export async function PhoneDetails({
    isRenewal = false,
    params,
    searchParams,
    contract,
    session,
}: Readonly<PhoneDetailsProps>) {
    let product: ProductParentFull | undefined;
    let tradeInBonusPlan: string | undefined;
    let productStatics: PalpatineProduct[];
    const { slug } = params;
    const type = WallType.PHONE;
    const isEasyTradeInEnabled = appConfig.features.easyTradeInActivated;
    try {
        [product, tradeInBonusPlan, { items: productStatics }] = await Promise.all([
            productDetailsService.getProductDetails(type, params.slug, isRenewal, false, session),
            cmsService.getBonusPlan(),
            cmsService.getProduct(),
        ]);
    } catch (error) {
        if (error instanceof ProductNotFoundError) {
            logger.log('🚨 Phone not found', slug);
            const wallPath = isRenewal ? paths.renewalPhoneWall : paths.phoneWall;
            const redirectInfo = handleProductNotFound(slug, WallType.PHONE, wallPath);
            permanentRedirect(redirectInfo.path);
        }
        redirect(paths.phoneWall);
    }

    const user = session?.user as UserType | undefined;
    const contractId = isRenewal ? contract?.id : undefined;
    const buyerContext = await personServices.getBuyerContext(user, contractId);
    const idPu = buyerContext.idPu;

    const { product: productWithStock, currentProduct } = await getCurrentProduct(product, params.slug, {
        ...buyerContext,
        contractId,
    });

    const animco = getProductAnimco(productStatics, product.urlKey, isRenewal);

    const urlSearchParams = new URLSearchParams(searchParams);

    const planType = getPlanTypeFromUrlKey(urlSearchParams.get('forfait'));

    const plans = await productDetailsService.getPlans(currentProduct.gencode, isRenewal, planType, contract, session);

    if (plans.length === 0 && (!isRenewal || (isRenewal && contract))) {
        logger.log('🚨 No plans found for product', currentProduct.gencode);
        redirect(paths.phoneWall);
    }

    return (
        <ProductDetail
            type={type}
            baseUrl={isRenewal ? paths.renewalPhoneWall : paths.phoneWall}
            searchParams={searchParams}
            product={productWithStock}
            plans={plans}
            currentProduct={currentProduct}
            planTypeId={planType}
            animco={animco}
            tradeInBonusPlan={tradeInBonusPlan}
            enableKonami
            contract={contract}
            session={session}
            isRenewal={isRenewal}
            slug={isRenewal ? PAGE_SLUG.PHONE_PRODUCT_RENEWAL : PAGE_SLUG.PHONE_PRODUCT}
            idPu={idPu}
            easyTradeInEnabled={isEasyTradeInEnabled}
        />
    );
}
